import type { TError, TPostBase, TPostType, TUpdatePost } from '@onesource/schemas';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';
import { getAvailableTagsBaseQueryKey } from '../tags/get-available-tags';
import { getSpacePostTypesQueryKey } from './get-space-post-types';

/**
 * Hook to update a post
 * Only the post author can update their own posts
 *
 * @param postId - Post ID (UUID) to update
 */
export const useUpdatePost = (postId: string) => {
  const queryClient = useQueryClient();

  return useMutation<TPostBase, TError, TUpdatePost>({
    mutationFn: async data => {
      const res = await axios.patch<TPostBase>(`/community-hub/posts/${postId}`, data);
      return res.data;
    },
    onSuccess: updated => {
      // Invalidate all posts queries to refresh the feed
      queryClient.invalidateQueries({ queryKey: [ENTITY, 'posts'] });
      // Edited tags can change which tags are "used", so refresh the filter list.
      queryClient.invalidateQueries({ queryKey: getAvailableTagsBaseQueryKey(updated.spaceId) });
      // Refresh the "Post type" filter's used-types list ONLY when the edit
      // introduces a type not already in the cached set — same logic as create.
      const postTypesKey = getSpacePostTypesQueryKey(updated.spaceId);
      const cachedTypes = queryClient.getQueryData<TPostType[]>(postTypesKey);
      if (updated.postType && cachedTypes && !cachedTypes.includes(updated.postType)) {
        queryClient.invalidateQueries({ queryKey: postTypesKey });
      }
    },
  });
};
